import React, { useEffect, useState } from 'react';
import {
    Box,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { green, red, blue } from '@mui/material/colors';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import MailIcon from '@mui/icons-material/Mail';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import {
    isInvitationExpired,
    getSchoolDeliveryState,
    deliveryErrorHint,
    getAutoResendState,
} from '../../formatUtils';
import { useInvitationSettings } from './useInvitationSettings';
import MailgunEventsDialog from '../../components/statistics/MailgunEventsDialog';

import {
    useTranslate,
    useRecordContext,
    useNotify,
    useRefresh,
    useResourceContext,
} from 'react-admin';

const ChangeUserStatus = ({ ...props }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const invitationSettings = useInvitationSettings();
    const handleRefresh = () => {
        refresh();
    };
    const record = useRecordContext();
    const resource = useResourceContext(props);
    const [processingButtons, setProcessingButtons] = useState({});
    const [open, setOpen] = useState(false);
    const [sendmail, setSendMail] = useState(true);
    const [remove_user, setRemoveUser] = useState(false);
    const [language, setLanguage] = useState('fi');
    const [shouldTriggerAction, setShouldTriggerAction] = useState(false);
    const [remove_groups, setRemoveGroups] = useState(true);
    const [eventsOpen, setEventsOpen] = useState(false);
    const [eventsMailgunId, setEventsMailgunId] = useState<string | null>(null);
    const [showActivationLink, setShowActivationLink] = useState(false);

    useEffect(() => {
        if (shouldTriggerAction) {
            handleAction(new Event('click'), record.guid);
            setShouldTriggerAction(false);
        }
    }, [shouldTriggerAction]);

    const {
        button: showButton = true,
        action = 'approve',
        school_guid,
        icon,
    } = props;

    const [checkedState, setCheckedState] = useState(
        record?.schools
            ?.filter(s => s.status !== 1)
            .reduce((acc, school) => {
                acc[school.guid] = school_guid
                    ? school.guid === school_guid
                    : true; // Default to checked if it matches school_guid or school_guid is undefined
                return acc;
            }, {})
    );

    const handleLanguageChange = event => {
        setLanguage(event.target.value);
    };

    const handleCheckboxChange = event => {
        const { name, checked } = event.target;
        setCheckedState(prevState => ({
            ...prevState,
            [name]: checked,
        }));
    };

    const handleSendMailCheckboxChange = event => {
        const { checked } = event.target;
        setSendMail(checked);
        //console.log('Send mail checked:', checked);
    };

    const handleRemoveTeacherCheckboxChange = event => {
        const { checked } = event.target;
        setRemoveUser(checked);
        //console.log('Remove teacher checked:', checked);
    };

    const handleRemoveGroupsCheckboxChange = event => {
        const { checked } = event.target;
        setRemoveGroups(checked);
        //console.log('Remove groups checked:', checked);
    };

    const handleAction = (e, guid) => {
        e.stopPropagation();
        setProcessingButtons(prevState => ({ ...prevState, [guid]: true }));

        //Find the checked schools
        let checkedSchools = [];
        for (const [key, value] of Object.entries(checkedState)) {
            if (value) {
                checkedSchools.push(key);
            }
        }

        //Call api to approve user and assign schools
        let data;
        switch (action) {
            case 'approve':
                data = {
                    action: `APPROVE_${resource.toUpperCase()}`,
                    user_guid: guid,
                    groups_to_approve: checkedSchools,
                    approve_user: true,
                    sendmail: sendmail,
                    lang: language,
                };
                break;
            case 'remove':
                data = {
                    action: `REMOVE_${resource.toUpperCase()}`,
                    user_guid: guid,
                    remove_user: remove_user,
                    groups_to_remove: checkedSchools,
                    remove_groups: remove_groups,
                };
                break;
            case 'unremove':
                data = {
                    action: `APPROVE_${resource.toUpperCase()}`,
                    user_guid: guid,
                    groups_to_approve: checkedSchools,
                    approve_user: false,
                    sendmail: false,
                    lang: language,
                };
                break;
            case 'invitation':
                data = {
                    action: `SEND_INVITATION`,
                    user_guid: guid,
                    schools: checkedSchools,
                    lang: language,
                };
                break;
            default:
                break;
        }
        console.log('Data to be sent:', JSON.stringify(data));
        console.log('Record:', JSON.stringify(resource));

        if (!data) {
            notify(translate(`resources.${resource}.actions.approve.error`), {
                type: 'error',
            });
            setProcessingButtons(prevState => ({
                ...prevState,
                [guid]: false,
            }));
            return;
        }
        // Send the request to the API
        fetch(`${process.env.REACT_APP_INFRA_API}/${resource}/${guid}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
            body: JSON.stringify(data),
        })
            .then(response => {
                if (response.status !== 200) {
                    throw new Error(response.statusText);
                }
                notify(
                    translate(`resources.${resource}.actions.approve.success`),
                    {
                        type: 'success',
                    }
                );
                handleRefresh();
                setProcessingButtons(prevState => ({
                    ...prevState,
                    [guid]: false,
                }));
            })
            .catch(() => {
                notify(
                    translate(`resources.${resource}.actions.approve.error`),
                    {
                        type: 'error',
                    }
                );
                setProcessingButtons(prevState => ({
                    ...prevState,
                    [guid]: false,
                }));
            });
    };

    const handleOpenDialog = e => {
        e.stopPropagation();
        setOpen(true);
    };

    const handleCloseDialog = e => {
        e.stopPropagation();
        setOpen(false);
        setShowActivationLink(false);
    };

    const handleContinue = e => {
        handleAction(e, record.guid);
        handleCloseDialog(e);
    };

    // Aktivointilinkki on kertakäyttöinen token; kopioidaan vain pyydettäessä, ei logata.
    const copyActivationLink = async (code: string) => {
        try {
            await navigator.clipboard.writeText(
                `https://nova.otava.fi/account/teacher/invitation/${code}`
            );
            notify('Kopioitu leikepöydälle', { type: 'success' });
        } catch (err) {
            notify('Kopiointi epäonnistui', { type: 'error' });
        }
    };

    if (!record) {
        return null;
    }

    return (
        <>
            {showButton && (
                <Button
                    key={`approve_${record.guid}`}
                    variant="contained"
                    size="small"
                    color="error"
                    title={props.title}
                    startIcon={
                        processingButtons[record.guid] ? (
                            <CircularProgress size={14} />
                        ) : null
                    }
                    onClick={handleOpenDialog}
                    disabled={processingButtons[record.guid]}
                >
                    {props.title ||
                        translate(
                            `resources.${resource}.actions.approve.button_title`
                        )}
                </Button>
            )}
            {!showButton &&
                action === 'approve' &&
                (record.status === 2 ||
                    (record?.schools?.filter(s => s.status === 2)?.length ??
                        0) > 0) && (
                    <IconButton
                        key={`approve_${record.guid}`}
                        size="small"
                        title={
                            props.title ||
                            translate(
                                `resources.${resource}.actions.approve.button_title`
                            )
                        }
                        onClick={handleOpenDialog}
                    >
                        <CheckCircleIcon color="error" fontSize="inherit" />
                    </IconButton>
                )}
            {!showButton &&
                action === 'remove' &&
                record.status !== 1 &&
                (record?.schools?.filter(s => s.status !== 1)?.length ?? 0) >
                    0 && (
                    <IconButton
                        key={`remove_${record.guid}`}
                        size="small"
                        title={
                            props.title ||
                            translate(
                                `resources.${resource}.actions.remove.button_title`
                            )
                        }
                        onClick={handleOpenDialog}
                    >
                        <RemoveCircleOutlineIcon
                            sx={{ color: red[500] }}
                            fontSize="inherit"
                        />
                    </IconButton>
                )}
            {!showButton &&
                action === 'unremove' &&
                record.status === 0 &&
                (record?.schools?.filter(s => s.status !== 0)?.length ?? 0) >
                    0 && (
                    <IconButton
                        key={`unremove_${record.guid}`}
                        size="small"
                        title={
                            props.title ||
                            translate(
                                `resources.${resource}.actions.unremove.button_title`
                            )
                        }
                        onClick={async e => {
                            e.stopPropagation();
                            setSendMail(false);
                            setLanguage('en');
                            setCheckedState({ [school_guid]: true });
                            setShouldTriggerAction(true);
                        }}
                    >
                        <AddCircleOutlineIcon
                            sx={{ color: green[500] }}
                            fontSize="inherit"
                        />
                    </IconButton>
                )}
            {!showButton && action === 'invitation' && (
                <IconButton
                    key={`invitation_${record.guid}`}
                    size="small"
                    title={
                        props.title ||
                        translate('resources.user.invitation.title')
                    }
                    onClick={handleOpenDialog}
                >
                    {icon || (
                        <MailIcon
                            sx={{ color: blue[500] }}
                            fontSize="inherit"
                        />
                    )}
                </IconButton>
            )}
            {action === 'approve' && (
                <Dialog
                    onClick={e => e.stopPropagation()}
                    open={open}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    maxWidth={'lg'}
                    sx={{ '& .MuiDialog-paper': { padding: 2 } }}
                >
                    <DialogTitle
                        id="alert-dialog-title"
                        sx={{ backgroundColor: 'primary.main', color: 'white' }}
                    >
                        {translate(
                            `resources.${resource}.actions.${action}.dialog.title`,
                            {
                                mode:
                                    record.status === 2
                                        ? 'tunnus'
                                        : 'koululiitos',
                            }
                        )}
                    </DialogTitle>

                    <DialogContent>
                        {record.status === 2 && (
                            <DialogContentText
                                id="alert-dialog-description"
                                sx={{ marginTop: 2 }}
                            >
                                {translate(
                                    `resources.${resource}.actions.${action}.dialog.content`,
                                    {
                                        name: `${record?.public.firstname} ${record?.public.lastname}`,
                                        username: `${record?.username}`,
                                    }
                                )}
                            </DialogContentText>
                        )}
                        {(record?.schools?.filter(s => s.status === 2)
                            ?.length ?? 0) > 0 && (
                            <DialogContentText sx={{ marginTop: 2 }}>
                                {translate(
                                    `resources.${resource}.actions.${action}.dialog.schools`
                                )}
                            </DialogContentText>
                        )}
                        {record?.schools?.map(school => (
                            <DialogContentText key={school.guid}>
                                {school.status === 2 && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    checkedState[school.guid]
                                                }
                                                onChange={handleCheckboxChange}
                                                name={school.guid}
                                                color="primary"
                                            />
                                        }
                                        label={school.name}
                                        key={school.guid}
                                    />
                                )}
                            </DialogContentText>
                        ))}
                    </DialogContent>

                    <DialogActions>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sendmail}
                                    onChange={handleSendMailCheckboxChange}
                                    name="sendmail"
                                    color="primary"
                                />
                            }
                            label={translate(
                                `resources.${resource}.actions.${action}.dialog.sendmail`
                            )}
                        />

                        <FormControl sx={{ width: 120 }}>
                            <InputLabel id="language-select-label">
                                {translate(
                                    `resources.${resource}.actions.${action}.dialog.language`
                                )}
                            </InputLabel>
                            <Select
                                labelId="language-select-label"
                                id="language-select"
                                disabled={!sendmail}
                                value={language}
                                label={translate(
                                    `resources.${resource}.actions.${action}.dialog.language`
                                )}
                                onChange={handleLanguageChange}
                            >
                                <MenuItem value="fi">Suomi</MenuItem>
                                <MenuItem value="sv">Ruotsi</MenuItem>
                                <MenuItem value="en">Englanti</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            onClick={handleCloseDialog}
                            color="primary"
                            variant="outlined"
                            autoFocus
                        >
                            {translate(
                                `resources.${resource}.actions.${action}.dialog.cancel`
                            )}
                        </Button>
                        <Button
                            onClick={handleContinue}
                            color="primary"
                            variant="contained"
                        >
                            {translate(
                                `resources.${resource}.actions.${action}.dialog.confirm`
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {action === 'remove' && (
                <Dialog
                    onClick={e => e.stopPropagation()}
                    open={open}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    maxWidth={'lg'}
                    sx={{ '& .MuiDialog-paper': { padding: 2 } }}
                >
                    <DialogTitle
                        id="alert-dialog-title"
                        sx={{
                            backgroundColor: 'error.main',
                            color: 'white',
                        }}
                    >
                        {translate(
                            `resources.${resource}.actions.${action}.dialog.title`,
                            {
                                mode:
                                    (record?.schools?.length ?? 0) > 0
                                        ? 'koululiitoksen'
                                        : 'tunnuksen',
                            }
                        )}
                    </DialogTitle>

                    <DialogContent>
                        {(record?.schools?.filter(s => s.status !== 1)
                            ?.length ?? 0) > 0 && (
                            <DialogContentText sx={{ marginTop: 2 }}>
                                {translate(
                                    `resources.${resource}.actions.${action}.dialog.schools`
                                )}
                            </DialogContentText>
                        )}
                        {record?.schools?.map(school => (
                            <DialogContentText key={school.guid}>
                                {school.status !== 1 && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    checkedState[school.guid]
                                                }
                                                onChange={handleCheckboxChange}
                                                name={school.guid}
                                                color="primary"
                                            />
                                        }
                                        label={school.name}
                                        key={school.guid}
                                    />
                                )}
                            </DialogContentText>
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Stack direction={'column'} spacing={0}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={remove_user}
                                        onChange={
                                            handleRemoveTeacherCheckboxChange
                                        }
                                        name="remove_user"
                                        color="primary"
                                    />
                                }
                                label={translate(
                                    `resources.${resource}.actions.${action}.dialog.remove_user`,
                                    {
                                        name: `${record?.public.firstname} ${record?.public.lastname}`,
                                        username: `${record?.username}`,
                                    }
                                )}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={remove_groups}
                                        onChange={
                                            handleRemoveGroupsCheckboxChange
                                        }
                                        name="remove_groups"
                                        color="primary"
                                    />
                                }
                                label={translate(
                                    `resources.${resource}.actions.${action}.dialog.remove_groups`
                                )}
                            />
                        </Stack>
                        <Button
                            onClick={handleCloseDialog}
                            color="primary"
                            variant="outlined"
                            autoFocus
                        >
                            {translate(
                                `resources.${resource}.actions.${action}.dialog.cancel`
                            )}
                        </Button>
                        <Button
                            onClick={handleContinue}
                            color="error"
                            variant="contained"
                        >
                            {translate(
                                `resources.${resource}.actions.${action}.dialog.confirm`
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {action === 'invitation' && open && (
                <Dialog
                    onClick={e => e.stopPropagation()}
                    open={open}
                    onClose={handleCloseDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    maxWidth={'lg'}
                    sx={{ '& .MuiDialog-paper': { padding: 2 } }}
                >
                    <DialogTitle
                        id="alert-dialog-title"
                        sx={{ backgroundColor: 'primary.main', color: 'white' }}
                    >
                        {translate('resources.user.invitation.dialog.title')}
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText sx={{ marginTop: 2 }}>
                            {translate(
                                'resources.user.invitation.dialog.content',
                                {
                                    name: `${record?.public.firstname} ${record?.public.lastname}`,
                                    username: `${record?.username}`,
                                }
                            )}
                        </DialogContentText>

                        {/* Display invitation count information */}
                        {record?.schools?.map(school => {
                            if (
                                school.status === 3 &&
                                school_guid === school.guid
                            ) {
                                const invitationCount =
                                    school.public?.invitations?.filter(
                                        invitation => invitation.status === 0
                                    )?.[0]?.counter ||
                                    (school.public?.invitations === undefined
                                        ? 0
                                        : 1);

                                const activeInvitation =
                                    school.public?.invitations?.filter(
                                        invitation => invitation.status === 0
                                    )?.[0];
                                const expired = isInvitationExpired(
                                    activeInvitation,
                                    invitationSettings
                                );
                                const delivery = getSchoolDeliveryState(
                                    school,
                                    invitationSettings
                                );
                                const autoResend = getAutoResendState(
                                    activeInvitation as any,
                                    invitationSettings
                                );

                                return (
                                    <React.Fragment key={school.guid}>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0',
                                                backgroundColor: '#fafafa',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                gutterBottom
                                            >
                                                {translate(
                                                    'resources.user.invitation.dialog.section_status'
                                                )}
                                            </Typography>
                                            <DialogContentText
                                                sx={{
                                                    color: 'primary.main',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {invitationCount > 0
                                                    ? translate(
                                                          'resources.user.invitation.dialog.sent_count',
                                                          {
                                                              count: invitationCount,
                                                          }
                                                      )
                                                    : translate(
                                                          'resources.user.invitation.dialog.not_sent'
                                                      )}
                                            </DialogContentText>
                                            {(activeInvitation as any)
                                                ?.first_sent ||
                                            (activeInvitation as any)
                                                ?.created ? (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                    variant="body2"
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.dialog.first_sent'
                                                    )}: ${new Date(
                                                        (
                                                            activeInvitation as any
                                                        ).first_sent ||
                                                            (
                                                                activeInvitation as any
                                                            ).created
                                                    ).toLocaleDateString(
                                                        'fi-FI'
                                                    )}`}
                                                </DialogContentText>
                                            ) : null}
                                            {(activeInvitation as any)
                                                ?.created &&
                                            (activeInvitation as any)
                                                ?.first_sent &&
                                            (activeInvitation as any)
                                                .created !==
                                                (activeInvitation as any)
                                                    .first_sent ? (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                    variant="body2"
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.dialog.last_sent'
                                                    )}: ${new Date(
                                                        (
                                                            activeInvitation as any
                                                        ).created
                                                    ).toLocaleDateString(
                                                        'fi-FI'
                                                    )}`}
                                                </DialogContentText>
                                            ) : null}
                                            {(activeInvitation as any)
                                                ?.last_auto_resend ? (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                    variant="body2"
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.dialog.last_auto_resend',
                                                        {
                                                            count:
                                                                (
                                                                    activeInvitation as any
                                                                )
                                                                    .auto_resend_count ||
                                                                0,
                                                        }
                                                    )}: ${new Date(
                                                        (
                                                            activeInvitation as any
                                                        ).last_auto_resend
                                                    ).toLocaleDateString(
                                                        'fi-FI'
                                                    )}`}
                                                </DialogContentText>
                                            ) : null}
                                            {expired && (
                                                <DialogContentText
                                                    sx={{
                                                        marginTop: 1,
                                                        color: 'error.main',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {translate(
                                                        'resources.user.invitation.dialog.expired'
                                                    )}
                                                </DialogContentText>
                                            )}
                                            {activeInvitation ? (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                    variant="body2"
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.dialog.auto_resend_label'
                                                    )}: ${
                                                        autoResend.eligible
                                                            ? translate(
                                                                  'resources.user.invitation.dialog.auto_resend_on'
                                                              )
                                                            : translate(
                                                                  `resources.user.invitation.dialog.auto_resend_off_${autoResend.reason}`
                                                              )
                                                    }`}
                                                </DialogContentText>
                                            ) : null}
                                            {autoResend.eligible &&
                                            autoResend.nextResendAt ? (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                    variant="body2"
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.dialog.auto_resend_next'
                                                    )}: ${
                                                        autoResend.nextResendAt.getTime() <=
                                                        Date.now()
                                                            ? translate(
                                                                  'resources.user.invitation.dialog.auto_resend_due'
                                                              )
                                                            : autoResend.nextResendAt.toLocaleDateString(
                                                                  'fi-FI'
                                                              )
                                                    }`}
                                                </DialogContentText>
                                            ) : null}
                                            {delivery.state !== 'none' && (
                                                <DialogContentText
                                                    sx={{ marginTop: 1 }}
                                                >
                                                    {`${translate(
                                                        'resources.user.invitation.delivery.label'
                                                    )}: ${translate(
                                                        `resources.user.invitation.delivery.${delivery.state}`
                                                    )}`}
                                                    {delivery.mailgunId && (
                                                        <Button
                                                            size="small"
                                                            sx={{ ml: 1 }}
                                                            onClick={() => {
                                                                setEventsMailgunId(
                                                                    delivery.mailgunId
                                                                );
                                                                setEventsOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            {translate(
                                                                'resources.user.invitation.delivery.show_events'
                                                            )}
                                                        </Button>
                                                    )}
                                                </DialogContentText>
                                            )}
                                            {delivery.state === 'failed' &&
                                                delivery.detail && (
                                                    <Table
                                                        size="small"
                                                        sx={{
                                                            mt: 1,
                                                            maxWidth: 640,
                                                        }}
                                                    >
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        borderBottom:
                                                                            'none',
                                                                        width: 130,
                                                                        verticalAlign:
                                                                            'top',
                                                                        color: 'error.main',
                                                                        px: 0,
                                                                        py: 0.25,
                                                                    }}
                                                                >
                                                                    {translate(
                                                                        'resources.user.invitation.delivery.error_category'
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        borderBottom:
                                                                            'none',
                                                                        color: 'error.main',
                                                                        py: 0.25,
                                                                    }}
                                                                >
                                                                    {deliveryErrorHint(
                                                                        delivery.detail
                                                                    )
                                                                        ? translate(
                                                                              deliveryErrorHint(
                                                                                  delivery.detail
                                                                              ) as string
                                                                          )
                                                                        : translate(
                                                                              'resources.user.invitation.delivery.errors.other'
                                                                          )}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        borderBottom:
                                                                            'none',
                                                                        verticalAlign:
                                                                            'top',
                                                                        color: 'text.secondary',
                                                                        px: 0,
                                                                        py: 0.25,
                                                                    }}
                                                                >
                                                                    {translate(
                                                                        'resources.user.invitation.delivery.error_raw'
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        borderBottom:
                                                                            'none',
                                                                        fontFamily:
                                                                            'monospace',
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        color: 'text.secondary',
                                                                        wordBreak:
                                                                            'break-word',
                                                                        py: 0.25,
                                                                    }}
                                                                >
                                                                    {
                                                                        delivery.detail
                                                                    }
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                )}
                                            {Array.isArray(
                                                (activeInvitation as any)
                                                    ?.manual_sends
                                            ) &&
                                                (activeInvitation as any)
                                                    .manual_sends.length >
                                                    0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            gutterBottom
                                                        >
                                                            {translate(
                                                                'resources.user.invitation.dialog.manual_sends'
                                                            )}
                                                        </Typography>
                                                        {[
                                                            ...(
                                                                activeInvitation as any
                                                            ).manual_sends,
                                                        ]
                                                            .reverse()
                                                            .map(
                                                                (
                                                                    entry: any,
                                                                    idx: number
                                                                ) => (
                                                                    <DialogContentText
                                                                        key={
                                                                            idx
                                                                        }
                                                                        sx={{
                                                                            marginTop: 0.5,
                                                                        }}
                                                                        variant="body2"
                                                                    >
                                                                        {`${new Date(
                                                                            entry.at
                                                                        ).toLocaleString(
                                                                            'fi-FI'
                                                                        )} — ${
                                                                            entry.by_name ||
                                                                            ''
                                                                        } (${translate(
                                                                            entry.source ===
                                                                                'nova'
                                                                                ? 'resources.user.invitation.dialog.manual_send_source_nova'
                                                                                : 'resources.user.invitation.dialog.manual_send_source_admin'
                                                                        )})`}
                                                                    </DialogContentText>
                                                                )
                                                            )}
                                                    </Box>
                                                )}
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography
                                            variant="subtitle2"
                                            gutterBottom
                                        >
                                            {translate(
                                                'resources.user.invitation.dialog.section_school'
                                            )}
                                        </Typography>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={true}
                                                    disabled={true}
                                                    name={school.guid}
                                                    color="primary"
                                                />
                                            }
                                            label={school.name}
                                        />
                                        {activeInvitation?.code && !expired && (
                                            <Stack
                                                direction="column"
                                                spacing={1}
                                                sx={{ mt: 2 }}
                                            >
                                                <Divider />
                                                <Typography variant="subtitle2">
                                                    {translate(
                                                        'resources.user.invitation.dialog.section_link'
                                                    )}
                                                </Typography>
                                                {!showActivationLink ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LinkIcon />}
                                                        onClick={() =>
                                                            setShowActivationLink(
                                                                true
                                                            )
                                                        }
                                                        sx={{
                                                            alignSelf:
                                                                'flex-start',
                                                        }}
                                                    >
                                                        {translate(
                                                            'resources.user.invitation.dialog.show_activation_link'
                                                        )}
                                                    </Button>
                                                ) : (
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={{
                                                            backgroundColor:
                                                                '#f5f5f5',
                                                            p: 1,
                                                            borderRadius: 1,
                                                            border: '1px solid #e0e0e0',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                wordBreak:
                                                                    'break-all',
                                                            }}
                                                        >
                                                            {`https://nova.otava.fi/account/teacher/invitation/${activeInvitation.code}`}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            title={translate(
                                                                'ra.action.copy'
                                                            )}
                                                            onClick={() =>
                                                                copyActivationLink(
                                                                    activeInvitation.code
                                                                )
                                                            }
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                )}
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {translate(
                                                        'resources.user.invitation.dialog.activation_link_help'
                                                    )}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </React.Fragment>
                                );
                            }
                            return null;
                        })}
                    </DialogContent>

                    <DialogActions>
                        <FormControl sx={{ width: 120 }}>
                            <InputLabel id="language-select-label">
                                {translate(
                                    'resources.user.invitation.dialog.language'
                                )}
                            </InputLabel>
                            <Select
                                labelId="language-select-label"
                                id="language-select"
                                value={language}
                                label={translate(
                                    'resources.user.invitation.dialog.language'
                                )}
                                onChange={handleLanguageChange}
                            >
                                <MenuItem value="fi">Suomi</MenuItem>
                                <MenuItem value="sv">Ruotsi</MenuItem>
                                <MenuItem value="en">Englanti</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            onClick={handleCloseDialog}
                            color="primary"
                            variant="outlined"
                            autoFocus
                        >
                            {translate(
                                'resources.user.invitation.dialog.cancel'
                            )}
                        </Button>
                        <Button
                            onClick={handleContinue}
                            color="primary"
                            variant="contained"
                        >
                            {translate(
                                'resources.user.invitation.dialog.confirm'
                            )}
                        </Button>
                    </DialogActions>
                    <MailgunEventsDialog
                        open={eventsOpen}
                        onClose={() => setEventsOpen(false)}
                        mailgunId={eventsMailgunId}
                    />
                </Dialog>
            )}
        </>
    );
};

export default ChangeUserStatus;
